import React, { useEffect, useState } from 'react';
import { Select, InputNumber, Input, Switch } from 'antd';
import moment from 'moment';
import useErrorMessage from '../../utils/ErrorMessage';
import useAuthContext from '../../contexts/AuthContext';

const { Option } = Select;

const useFields = (t, insurer, purpose = 'create', form) => {
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [enums, setEnums] = useState({});
  const [postalCode, setPostalCode] = useState();
  const [cities, setCities] = useState();
  const [oriasStatus, setOriasStatus] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/insurers/enums' });
      setEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getCity = async (postal_code) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/insee/cities/${postal_code}`
      });
      setCities(data);
      return data;
    } catch (e) {
      if (e.response) message(e.response.status);
      return [];
    }
  };

  const handlePostalCodeChange = async (event) => {
    const { value } = event.target;

    if (value && value.length >= 2) {
      setPostalCode(value);
      const tmpCities = await getCity(value.slice(0, 2));

      if (tmpCities) {
        const filteredCities = tmpCities.filter((city) =>
          city.code.startsWith(value)
        );

        if (filteredCities.length === 1) {
          form.setFieldsValue({ city: filteredCities[0].nom });
        }
      } else {
        form.setFieldsValue({ city: undefined });
      }
    }
  };

  const getSiretInfo = async (code) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/insee/${code}`
      });

      const {
        numeroVoieEtablissement,
        typeVoieEtablissement,
        libelleVoieEtablissement,
        codePostalEtablissement,
        complementAdresseEtablissement
      } = data.etablissement.adresseEtablissement;

      const {
        denominationUniteLegale,
        prenomUsuelUniteLegale,
        nomUniteLegale,
        sexeUniteLegale
      } = data.etablissement.uniteLegale;

      const { siren, nic } = data.etablissement;

      let tmpGender;
      if (sexeUniteLegale === 'M') {
        tmpGender = 'MISTER';
      }
      if (sexeUniteLegale === 'F') {
        tmpGender = 'MADAME';
      }

      form.setFieldsValue({
        name: denominationUniteLegale || nomUniteLegale,
        number: numeroVoieEtablissement,
        street: `${typeVoieEtablissement} ${libelleVoieEtablissement}`,
        postal_code: codePostalEtablissement,
        additionnal: complementAdresseEtablissement,
        owner_first_name: prenomUsuelUniteLegale,
        owner_last_name: nomUniteLegale,
        owner_gender: tmpGender
      });

      if (form.getFieldValue('siret_number').length < 14) {
        form.setFieldsValue({
          siret_number: `${siren}${nic}`
        });
      }
    } catch (e) {
      if (e.response) message('unknown_siren_siret');
    }
  };

  const handleSiretChange = async (event) => {
    const { value } = event.target;

    if (value.length === 9 || value.length === 14) {
      setIsLoading(true);
      await getSiretInfo(value);
      setIsLoading(false);
    }
  };

  const getOriasInfo = async (number) => {
    try {
      const { data } = await dispatchAPI('GET', { url: `/orias/${number}` });
      const exists = data.find((element) => element.key === 'foundInRegistry')
        .value;

      if (exists === 'false') {
        setOriasStatus('INEXISTANT');
      }

      const siren = data.find((element) => element.key === 'siren');
      const categoryIndex = data.indexOf(
        data.find(
          (element) => element.key === 'categoryName' && element.value === 'COA'
        )
      );
      const status = data[categoryIndex + 1].value;
      setOriasStatus(status);

      if (siren) {
        form.setFieldsValue({
          siret_number: siren.value
        });
        await handleSiretChange({ target: { value: siren.value } });
      }
    } catch (e) {
      if (e.response) message('orias_unavailable');
    }
  };

  useEffect(() => {
    (async () => {
      await getEnums();
    })();
    // eslint-disable-next-line
  }, []);

  const handleOriasChange = async (event) => {
    const { value } = event.target;

    if (value.length === 8) {
      await getOriasInfo(value);
    }
  };

  const getLayoutFromOriasStatus = (status) => {
    switch (status) {
      case 'INSCRIT':
        return {
          validateStatus: 'success',
          help: t('insurers.form.orias_success')
        };
      case 'SUPPRIME':
        return {
          validateStatus: 'error',
          help: t('insurers.form.orias_error')
        };
      case 'INEXISTANT':
        return {
          validateStatus: 'error',
          help: t('insurers.form.orias_not_found')
        };
      default:
        return null;
    }
  };

  const getFields = () => {
    const fields = [];

    if (insurer) {
      fields.push(
        {
          name: ['created_at'],
          label: insurer && t('insurers.form.created_at'),
          content: insurer && moment(insurer.created_at).format('DD/MM/YYYY')
        },
        {
          name: ['ac_number'],
          label: insurer && t('insurers.form.ac_number'),
          content: insurer && insurer.ac_number
        }
      );
    }

    fields.push(
      {
        name: ['orias_number'],
        rules: [{ required: true }],
        label: insurer && t('insurers.form.orias_number'),
        content: insurer && insurer.orias_number,
        input: <Input onChange={handleOriasChange} id="success" disabled />,
        hasFeedback: oriasStatus,
        layout: oriasStatus && getLayoutFromOriasStatus(oriasStatus),
        tooltip: true
      },
      {
        name: ['siret_number'],
        rules: [{ required: true }],
        label: insurer && t('insurers.form.siret_number'),
        content: insurer && insurer.siret_number,
        input: <Input onChange={handleSiretChange} disabled />,
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' },
        tooltip: true
      },
      {
        name: ['iban'],
        rules: [{ required: true }],
        label: insurer && t('insurers.form.iban'),
        content: insurer && insurer.iban,
        input: <Input onChange={handleSiretChange} disabled />,
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' },
        tooltip: true
      },
      {
        name: ['bic'],
        rules: [{ required: true }],
        label: insurer && t('insurers.form.BIC'),
        content: insurer && insurer.bic,
        input: <Input disabled />,
        tooltip: true
      },
      {
        name: ['name'],
        rules: [{ required: true }],
        label: insurer && t('insurers.form.name'),
        content: insurer && insurer.name,
        input: <Input disabled />,
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' },
        tooltip: true
      },
      {
        name: ['email'],
        rules: [
          { required: true },
          {
            type: 'email',
            message: t('errors.form.EMAIL')
          }
        ],
        label: insurer && t('insurers.form.email'),
        content: insurer && insurer.email
      },
      {
        name: ['phone_number'],
        label: insurer && t('insurers.form.phone_number'),
        content: insurer && insurer.phone_number
      },
      {
        name: ['number'],
        label: insurer ? 'insurers.form.address.number' : 'address.number',
        content: insurer && insurer.address && insurer.address.number,
        input: <InputNumber />,
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' }
      },
      {
        name: ['street'],
        label: insurer ? 'insurers.form.address.street' : 'address.street',
        content: insurer && insurer.address && insurer.address.street,
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' }
      },
      {
        name: ['additional'],
        label: insurer
          ? 'insurers.form.address.additional'
          : 'address.additional',
        content: insurer && insurer.address && insurer.address.additional,
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' }
      },
      {
        name: ['postal_code'],
        label: insurer
          ? 'insurers.form.address.postal_code'
          : 'address.postal_code',
        content: insurer && insurer.address && insurer.address.postal_code,
        input: (
          <Input
            style={{ width: '90px' }}
            onChange={handlePostalCodeChange}
            autoComplete="off"
          />
        ),
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' }
      },
      {
        name: ['city'],
        label: insurer ? 'insurers.form.address.city' : 'address.city',
        content: insurer && insurer.address && insurer.address.city,
        input: (
          <Select
            showSearch
            onSelect={(selected) => {
              form.setFieldsValue({
                postal_code: cities.find((city) => city.nom === selected).code
              });
            }}
            options={
              cities &&
              cities
                .filter((city) => city.code.startsWith(postalCode))
                .map((city) => ({
                  label: `${city.nom} (${city.code})`,
                  value: city.nom
                }))
            }
          />
        ),
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' }
      }
    );
    fields.push(
      {
        name: ['owner_gender'],
        rules: [{ required: true }],
        input: (
          <Select>
            {enums.genders &&
              enums.genders.map((g) => (
                <Option value={g} key={g}>
                  {t(`insurers.form.${g}`)}
                </Option>
              ))}
          </Select>
        ),
        label: insurer && t('insurers.form.owner_gender'),
        content:
          insurer &&
          insurer.owner_gender &&
          t(`insurers.form.${insurer.owner_gender}`),
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' }
      },
      {
        name: ['owner_first_name'],
        rules: [{ required: true }],
        label: insurer && t('insurers.form.owner_first_name'),
        content: insurer && insurer.owner_first_name,
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' }
      },
      {
        name: ['owner_last_name'],
        rules: [{ required: true }],
        label: insurer && t('insurers.form.owner_last_name'),
        content: insurer && insurer.owner_last_name,
        hasFeedback: isLoading,
        layout: isLoading && { validateStatus: 'validating' }
      },
      {
        name: ['owner_email'],
        rules: [
          { required: true },
          {
            type: 'email',
            message: t('errors.form.EMAIL')
          }
        ],
        label: insurer && t('insurers.form.owner_email'),
        content: insurer && insurer.owner_email
      },
      {
        name: ['owner_phone_number'],
        label: insurer && t('insurers.form.owner_phone_number'),
        content: insurer && insurer.owner_phone_number
      },
      {
        name: ['quote_automail'],
        label: insurer && t('insurers.form.quote_automail'),
        content: insurer && insurer.quote_automail,
        valuePropName: 'checked',
        input: (
          <Switch
            checkedChildren={t('quotes.form.switch.yes')}
            unCheckedChildren={t('quotes.form.switch.no')}
          />
        )
      }
    );

    if (purpose === 'edit' || insurer) {
      fields.push({
        name: ['url'],
        label: insurer && t('insurers.form.url'),
        content: insurer && insurer.url && (
          <a
            href={
              insurer.url.startsWith('http')
                ? insurer.url
                : `https://${insurer.url}`
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            {insurer.url.startsWith('http')
              ? insurer.url
              : `https://${insurer.url}`}
          </a>
        )
      });
    }
    if (insurer) {
      fields.push(
        {
          name: ['employees_count'],
          label: t('insurers.form.employees_count'),
          content: insurer && insurer.employees_count
        },
        {
          name: ['agencies_count'],
          label: t('insurers.form.agencies_count'),
          content: insurer && insurer.agencies_count
        },
        {
          name: ['group'],
          label: t('insurers.form.group'),
          content: (insurer && insurer.group?.name) || '—'
        },
        {
          name: ['group_membership_date'],
          label: t('insurers.form.group_membership_date'),
          content:
            insurer && insurer.group_membership_date
              ? moment(insurer.group_membership_date).format('MM/YYYY')
              : '—'
        },
        {
          name: ['group_head'],
          label: t('insurers.form.group_head'),
          content: (insurer && insurer.group?.head_insurer?.name) || '—'
        }
      );
    }
    return fields;
  };

  return {
    getFields
  };
};

export default useFields;
